import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'es' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    home: 'Home',
    aboutMe: 'About Me',
    practiceAreas: 'Practice Areas',
    blog: 'Blog',
    multimedia: 'Multimedia',
    clerk: 'Clerk',
    contact: 'Contact',
    
    // Multimedia
    multimediaTitle: 'Multimedia',
    multimediaDescription: 'Events, conferences, and professional engagements.',
    multimediaEmpty: 'No media yet',
    multimediaEmptyHint: 'Add videos to public/videos/ and images to public/images/multimedia/, then list them in public/multimedia.json.',
    multimediaImageCompetitionLawDialogDo:
      '«Clear rules to compete: dialogue on the reform of the Competition Law in the Dominican Republic» 🇩🇴. This dialogue is a space for valuable contributions that strengthen the analysis and the building of a more solid and transparent competition framework.',
    
    // Article actions
    downloadPDF: 'Download PDF',
    showCitation: 'Cite Article',
    thesisDownloadLabel: 'Download full thesis',
    thesisExcerptNote: 'This article is an excerpt from my undergraduate thesis, which you can download in full using the button above.',
    citationModalTitle: 'APA 7 Citation',
    citationFor: 'Citation for:',
    copyCitation: 'Copy Citation',
    copied: 'Copied!',
    citationNote: 'This citation follows the APA 7th edition format for blog posts and online articles. Always verify with your institution\'s citation guidelines.',
    downloadingPDF: 'Generating PDF...',
    
    // Home Page
    greeting: "Hello, I'm Joan Bisonó,",
    profession: 'Attorney & Consultant — Digital Law, Financial Markets & Economic Regulation',
    contactMe: 'Contact Me',
    
    // About Page
    aboutGreeting: 'Hello',
    aboutIm: "I'm",
    aboutName: 'Joan Bisonó',
    aboutRole: 'Attorney & Consultant in Digital Law, Financial Markets and Economic Regulation',
    professionalProfile: 'Professional Profile',
    objective: 'Objective',
    academicBackground: 'Academic Background',
    scheduleCall: 'Schedule a Call',
    latestPublication: 'Latest Publication',
    clerkBannerLabel: 'For Claude and ChatGPT',
    clerkBannerTitle: 'Clerk',
    clerkBannerDescription: 'Dominican case law and legislation, inside your assistant',
    clerkBannerCta: 'See how it works',
    
    // Professional Profile Content
    profilePara1: "I am Joan Bisonó, a lawyer graduated Magna Cum Laude from Pontificia Universidad Católica Madre y Maestra (PUCMM). I have experience in regulatory compliance for capital markets entities and coordination of legal and corporate activities. I worked as a Senior Paralegal at the international firm ECIJA DVMS, managing high-profile client relationships and implementing technological solutions to optimize legal processes. I am currently an attorney at Jorge Prats & Sousa Duverge Abogados | Consultores and am pursuing a Master's degree in Administrative Law and Economic Regulation at PUCMM.",
    profilePara2: "I am also a researcher and academic writer with publications on topics such as blockchain dispute resolution, fintech regulation, and algorithmic adjudication in crypto-arbitration platforms.",
    
    // Objective Content
    objectiveText: "To leverage my expertise in digital law, financial markets, and economic regulation to provide innovative legal solutions at the intersection of technology and law. My goal is to apply my knowledge in regulatory technology, blockchain, and fintech to help clients navigate the evolving legal landscape of emerging technologies.",
    
    // Professional Info
    phone: 'Phone',
    email: 'Email',
    
    // Education
    pucmmName: 'Pontificia Universidad Católica Madre y Maestra',
    pucmmDegree: 'Law Degree (Licenciatura en Derecho)',
    pucmmPeriod: 'Sep 2021 - Sep 2025 • Magna Cum Laude',
    omgName: 'Pontificia Universidad Católica Madre y Maestra (PUCMM)',
    omgDegree: "Master's in Administrative Law and Economic Regulation (Maestría en Derecho Administrativo y Regulación Económica)",
    omgPeriod: 'Apr 2026 - Present',
    
    // Key Credentials
    keyCredentials: 'Key Credentials',
    education: 'Education',
    educationMasters: 'PUCMM — Master\'s in Administrative Law and Economic Regulation',
    educationBachelors: 'PUCMM — LL.B., Magna Cum Laude',
    currentRole: 'Current Role',
    currentRoleDescription: 'Attorney & Consultant at Jorge Prats & Sousa Duverge Abogados | Consultores, specializing in Digital Law, Financial Markets and Economic Regulation',
    affiliations: 'Affiliations',
    affiliationKleros: 'Kleros Fellow — 9th Cohort, Kleros Fellowship of Justice',
    affiliationBlf: 'Member, Blockchain Lawyers Forum',
    
    // Profile Summary
    profileSummary: 'Attorney & Consultant in Digital Law, Financial Markets, and Economic Regulation. Expertise in regulatory compliance and capital markets.',
    focusAreas: 'Focus Areas:',
    focusArea1: 'Regulatory technology (RegTech) and supervision of fintech ecosystems',
    focusArea2: 'Blockchain dispute resolution and crypto-arbitration platforms',
    focusArea3: 'Capital markets regulatory compliance and financial regulation',
    focusArea4: 'Academic research on emerging technologies in law and financial regulation',
    
    // Blog
    blogTitle: 'Blog',
    blogDescription: 'Insights and articles on digital law, dispute resolution and law and economics',
    newArticle: 'New Article',
    articleTitle: 'Article Title',
    articleContent: 'Article Content',
    publishArticle: 'Publish Article',
    cancel: 'Cancel',
    noArticles: 'No articles published yet',
    searchPlaceholder: 'Search articles by title, keywords...',
    noSearchResults: 'No articles found for this search. Try different keywords.',
    paginationPage: 'Page',
    paginationOf: 'of',
    goToPagePlaceholder: 'Go to page',
    goToPageButton: 'Go',
    readMore: 'Read More',
    backToBlog: 'Back to Blog',
    seeMore: 'See More',
    seeLess: 'See Less',
    moreArticles: 'More Articles',
    articleBylinePrefix: 'By',
    authorBio: 'is an attorney and consultant specializing in Digital Law, Financial Markets, Economic Regulation, Legal Tech, and AI Regulation & Governance. He obtained a Bachelor of Laws (LL.B.) from the Pontificia Universidad Católica Madre y Maestra (PUCMM) and is currently pursuing a Master\'s Degree (LL.M.) in Administrative Law and Economic Regulation at PUCMM. ORCID: 0009-0006-2365-1334.',
    authorAlisonBio: 'is a practicing attorney and legal researcher at the multinational firm ECIJA DVMS, specializing in Civil and Commercial Litigation and Arbitration. She earned a Bachelor of Laws (LL.B.) from the Universidad Iberoamericana (UNIBE) and is currently a Master\'s Degree (LL.M.) Candidate in Civil Procedure at the Pontificia Universidad Católica Madre y Maestra (PUCMM). Contact: alisonsofiaaa@gmail.com.',
    articleDisclaimer: 'The content of this article represents my personal views and does not reflect the official policy or position of my current or former employers. Furthermore, the information provided herein is for general informational purposes only and does not constitute professional or legal advice.',
    writtenBy: 'Written by',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    blogTopics: 'Topics',
    blogAllTopics: 'All',
    blogAuthorBlurb: 'Attorney. Digital law, financial markets, economic regulation and legal tech.',
    
    
    // Contact Page
    contactDescription: 'Get in touch to discuss your legal needs and schedule a consultation.',
    getInTouch: 'Get in Touch',
    contactIntro: 'Whether you need legal advice, representation, or have questions about my services, I\'m here to help. Feel free to reach out through any of the channels below.',
    followMe: 'Follow me on social media',
    scheduleConsultation: 'Schedule a Consultation',
    scheduleDescription: 'Book a personalized consultation to discuss your legal matters. I offer flexible scheduling to accommodate your needs.',
    callNow: 'Call Now',
    sendEmail: 'Send Email',
    availability: 'Availability',
    mondayFriday: 'Monday - Friday',
    responseTime: 'Response Time',
    within24Hours: 'Within 24 hours',
    legalConsultation: 'Legal Consultation',
    consultationDescription: 'Professional legal advice tailored to your specific situation in litigation, arbitration, and dispute resolution.',
    corporateServices: 'Corporate Services',
    corporateDescription: 'Comprehensive legal support for businesses, including contract review, compliance, and corporate litigation.',
    
    // Practice Areas Page
    practiceAreasDescription: 'Innovative legal services at the intersection of law and technology, specializing in emerging technologies and digital transformation.',
    consultNow: 'Consult Now',
    needLegalAdvice: 'Need Legal Advice?',
    needLegalAdviceDescription: 'Get in touch to discuss how I can help you with your legal matters.',
    
    // Practice Area 1: Digital Law
    practiceArea1Title: 'Digital Law',
    practiceArea1Description: 'Legal advisory on digital transformation, data protection, cybersecurity, e-commerce, and digital rights. Comprehensive guidance for businesses navigating the digital landscape.',
    
    // Practice Area 2: Financial Markets
    practiceArea2Title: 'Financial Markets',
    practiceArea2Description: 'Legal advisory on capital markets regulation, securities compliance, investment fund structuring, and financial instruments. Comprehensive guidance for entities navigating financial regulatory frameworks.',
    
    // Practice Area 3: Legal Strategy
    practiceArea3Title: 'Legal Strategy',
    practiceArea3Description: 'Strategic legal planning and consulting for businesses and individuals. Development of comprehensive legal strategies aligned with business objectives and risk management.',
    
    // Practice Area 4: Commercial Law
    practiceArea4Title: 'Commercial Law',
    practiceArea4Description: 'Comprehensive legal support for commercial transactions, business contracts, corporate compliance, and commercial dispute resolution. Advisory for businesses of all sizes.',
    
    // Practice Area 5: Corporate Law
    practiceArea5Title: 'Corporate Law',
    practiceArea5Description: 'Corporate structuring, mergers and acquisitions, corporate governance, and compliance. Strategic legal counsel for corporations, startups, and capital markets entities.',
    
    // Practice Area 6: Online Dispute Resolution
    practiceArea6Title: 'Online Dispute Resolution',
    practiceArea6Description: 'Representation in online and blockchain-based dispute resolution platforms. Expertise in ODR systems, smart contract disputes, and decentralized arbitration protocols.',
    
    // Practice Area 7: Computational Law
    practiceArea7Title: 'Computational Law',
    practiceArea7Description: 'Application of computational methods to legal problems. Smart contracts, algorithmic regulation, legal analytics, and automation of legal decision-making processes.',
    
    // Practice Area 8: Artificial Intelligence Governance
    practiceArea8Title: 'Artificial Intelligence Governance',
    practiceArea8Description: 'Legal frameworks for AI development and deployment. AI ethics, algorithmic accountability, bias mitigation, and compliance with emerging AI regulations.',
    
    // Practice Area 9: Crypto Governance
    practiceArea9Title: 'Crypto Governance',
    practiceArea9Description: 'Legal advisory on cryptocurrency, blockchain, DeFi, NFTs, and digital assets. Regulatory compliance, tokenomics, and governance structures for crypto projects.',
    
    // Footer
    footerDescription: 'Professional legal services with expertise in digital law, fintech regulation, and capital markets compliance. Committed to providing innovative legal solutions at the intersection of technology and law.',
    quickLinks: 'Quick Links',
    contactInfo: 'Contact Information',
    allRightsReserved: 'All rights reserved.',
    languageLabel: 'Language',
    footerLinksLabel: 'Footer links',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
  },
  es: {
    // Navigation
    home: 'Inicio',
    aboutMe: 'Sobre Mí',
    practiceAreas: 'Áreas de Práctica',
    blog: 'Blog',
    multimedia: 'Multimedia',
    clerk: 'Clerk',
    contact: 'Contacto',
    
    // Multimedia
    multimediaTitle: 'Multimedia',
    multimediaDescription: 'Eventos, conferencias y participación profesional.',
    multimediaEmpty: 'Aún no hay contenido',
    multimediaEmptyHint: 'Añade videos en public/videos/ e imágenes en public/images/multimedia/, y listálos en public/multimedia.json.',
    multimediaImageCompetitionLawDialogDo:
      '«Reglas claras para competir: diálogo sobre la reforma de la Ley de Competencia en la República Dominicana» 🇩🇴. Este diálogo constituye un espacio de valiosos aportes que fortalecen el análisis y la construcción de un marco de competencia más sólido y transparente.',
    
    // Article actions
    downloadPDF: 'Descargar PDF',
    showCitation: 'Citar Artículo',
    thesisDownloadLabel: 'Descargar tesis completa',
    thesisExcerptNote: 'Este artículo es un extracto de mi tesis de grado, que puedes descargar completa usando el botón de arriba.',
    citationModalTitle: 'Cita APA 7',
    citationFor: 'Cita para:',
    copyCitation: 'Copiar Cita',
    copied: '¡Copiado!',
    citationNote: 'Esta cita sigue el formato APA 7ª edición para publicaciones de blog y artículos en línea. Siempre verifica con las pautas de citación de tu institución.',
    downloadingPDF: 'Generando PDF...',
    
    // Home Page
    greeting: 'Hola, soy Joan Bisonó,',
    profession: 'Abogado y Consultor — Derecho Digital, Mercados Financieros y Regulación Económica',
    contactMe: 'Contáctame',
    
    // About Page
    aboutGreeting: 'Hola',
    aboutIm: 'soy',
    aboutName: 'Joan Bisonó',
    aboutRole: 'Abogado y Consultor en Derecho Digital, Mercados Financieros y Regulación Económica',
    professionalProfile: 'Perfil Profesional',
    objective: 'Objetivo',
    academicBackground: 'Formación Académica',
    scheduleCall: 'Programar una Llamada',
    latestPublication: 'Última Publicación',
    clerkBannerLabel: 'Para Claude y ChatGPT',
    clerkBannerTitle: 'Clerk',
    clerkBannerDescription: 'IA legal con la jurisprudencia y la legislación dominicanas, dentro de tu asistente',
    clerkBannerCta: 'Ver cómo funciona',
    
    // Professional Profile Content
    profilePara1: "Soy Joan Bisonó, abogado egresado Magna Cum Laude de la Pontificia Universidad Católica Madre y Maestra (PUCMM). Tengo experiencia en cumplimiento regulatorio para entidades de mercados de capitales y coordinación de actividades jurídicas y corporativas. También trabajé como Paralegal Senior en la firma internacional ECIJA DVMS, gestionando relaciones con clientes de alto perfil e implementando soluciones tecnológicas para optimizar procesos jurídicos. Actualmente curso una maestría en Derecho Administrativo y Regulación Económica en la PUCMM.",
    profilePara2: "También soy investigador y escritor académico con publicaciones sobre temas como resolución de disputas en blockchain, regulación fintech y adjudicación algorítmica en plataformas de criptoarbitraje.",
    
    // Objective Content
    objectiveText: "Aprovechar mi experiencia en derecho digital, mercados financieros y regulación económica para proporcionar soluciones legales innovadoras en la intersección de la tecnología y el derecho. Mi objetivo es aplicar mis conocimientos en tecnología regulatoria, blockchain y fintech para ayudar a los clientes a navegar el panorama legal en evolución de las tecnologías emergentes.",
    
    // Professional Info
    phone: 'Teléfono',
    email: 'Correo',
    
    // Education
    pucmmName: 'Pontificia Universidad Católica Madre y Maestra',
    pucmmDegree: 'Licenciatura en Derecho',
    pucmmPeriod: 'Sep 2021 - Sep 2025 • Magna Cum Laude',
    omgName: 'Pontificia Universidad Católica Madre y Maestra (PUCMM)',
    omgDegree: 'Maestría en Derecho Administrativo y Regulación Económica',
    omgPeriod: 'Abr 2026 - Presente',
    
    // Key Credentials
    keyCredentials: 'Credenciales Clave',
    education: 'Educación',
    educationMasters: 'PUCMM — Maestría en Derecho Administrativo y Regulación Económica',
    educationBachelors: 'PUCMM — Licenciatura, Magna Cum Laude',
    currentRole: 'Rol Actual',
    currentRoleDescription: 'Abogado y Consultor en Jorge Prats & Sousa Duverge Abogados | Consultores, especializado en Derecho Digital, Mercados Financieros y Regulación Económica',
    affiliations: 'Afiliaciones',
    affiliationKleros: 'Kleros Fellow — 9.ª Cohorte, Kleros Fellowship of Justice',
    affiliationBlf: 'Miembro, Blockchain Lawyers Forum',
    
    // Profile Summary
    profileSummary: 'Abogado y Consultor en Derecho Digital, Mercados Financieros y Regulación Económica. Experiencia en cumplimiento regulatorio y mercados de capitales.',
    focusAreas: 'Áreas de Enfoque:',
    focusArea1: 'Tecnología regulatoria (RegTech) y supervisión de ecosistemas fintech',
    focusArea2: 'Resolución de disputas en blockchain y plataformas de criptoarbitraje',
    focusArea3: 'Cumplimiento regulatorio de mercados de capitales y regulación financiera',
    focusArea4: 'Investigación académica sobre tecnologías emergentes en derecho y regulación financiera',
    
    // Blog
    blogTitle: 'Blog',
    blogDescription: 'Perspectivas y artículos sobre derecho digital, resolución de controversias y análisis económico del derecho',
    newArticle: 'Nuevo Artículo',
    articleTitle: 'Título del Artículo',
    articleContent: 'Contenido del Artículo',
    publishArticle: 'Publicar Artículo',
    cancel: 'Cancelar',
    noArticles: 'Aún no se han publicado artículos',
    searchPlaceholder: 'Buscar artículos por título, palabras clave...',
    noSearchResults: 'No se encontraron artículos. Prueba con otras palabras.',
    paginationPage: 'Página',
    paginationOf: 'de',
    goToPagePlaceholder: 'Ir a página',
    goToPageButton: 'Ir',
    readMore: 'Leer Más',
    backToBlog: 'Volver al Blog',
    seeMore: 'Ver Más',
    seeLess: 'Ver Menos',
    moreArticles: 'Más Artículos',
    articleBylinePrefix: 'Por',
    authorBio: 'es abogado y consultor especializado en Derecho Digital, Mercados Financieros, Regulación Económica, Legal Tech y Regulación y Gobernanza de Inteligencia Artificial. Obtuvo el título de Licenciado en Derecho (LL.B.) en la Pontificia Universidad Católica Madre y Maestra (PUCMM) y actualmente cursa la Maestría (LL.M.) en Derecho Administrativo y Regulación Económica en la PUCMM. ORCID: 0009-0006-2365-1334.',
    authorAlisonBio: 'es abogada en ejercicio e investigadora jurídica en la firma multinacional ECIJA DVMS, especializada en Litigación Civil y Comercial y Arbitraje. Obtuvo el título de Licenciada en Derecho (LL.B.) en la Universidad Iberoamericana (UNIBE) y actualmente es candidata a la Maestría (LL.M.) en Derecho Procesal Civil en la Pontificia Universidad Católica Madre y Maestra (PUCMM). Contacto: alisonsofiaaa@gmail.com.',
    articleDisclaimer: 'El contenido de este artículo representa mi opinión personal y no refleja la política o posición oficial de mis empleadores actuales o anteriores. Asimismo, la información proporcionada tiene fines meramente informativos y no constituye asesoramiento profesional ni jurídico.',
    writtenBy: 'Escrito por',
    copyLink: 'Copiar enlace',
    linkCopied: 'Enlace copiado',
    blogTopics: 'Temas',
    blogAllTopics: 'Todos',
    blogAuthorBlurb: 'Abogado. Derecho digital, mercado de valores, regulación económica y legal tech.',
    
    
    // Contact Page
    contactDescription: 'Ponte en contacto para discutir tus necesidades legales y programar una consulta.',
    getInTouch: 'Ponte en Contacto',
    contactIntro: 'Ya sea que necesites asesoría legal, representación o tengas preguntas sobre mis servicios, estoy aquí para ayudarte. No dudes en contactarme a través de cualquiera de los canales a continuación.',
    followMe: 'Sígueme en redes sociales',
    scheduleConsultation: 'Programa una Consulta',
    scheduleDescription: 'Reserva una consulta personalizada para discutir tus asuntos legales. Ofrezco horarios flexibles para adaptarme a tus necesidades.',
    callNow: 'Llamar Ahora',
    sendEmail: 'Enviar Correo',
    availability: 'Disponibilidad',
    mondayFriday: 'Lunes - Viernes',
    responseTime: 'Tiempo de Respuesta',
    within24Hours: 'Dentro de 24 horas',
    legalConsultation: 'Consulta Legal',
    consultationDescription: 'Asesoría legal profesional adaptada a tu situación específica en litigios, arbitraje y resolución de controversias.',
    corporateServices: 'Servicios Corporativos',
    corporateDescription: 'Apoyo legal integral para empresas, incluyendo revisión de contratos, cumplimiento normativo y litigios corporativos.',
    
    // Practice Areas Page
    practiceAreasDescription: 'Servicios legales innovadores en la intersección del derecho y la tecnología, especializados en tecnologías emergentes y transformación digital.',
    consultNow: 'Consulta Ahora',
    needLegalAdvice: '¿Necesitas Asesoría Legal?',
    needLegalAdviceDescription: 'Ponte en contacto para discutir cómo puedo ayudarte con tus asuntos legales.',
    
    // Practice Area 1: Digital Law
    practiceArea1Title: 'Derecho Digital',
    practiceArea1Description: 'Asesoría legal en transformación digital, protección de datos, ciberseguridad, comercio electrónico y derechos digitales. Orientación integral para empresas navegando el panorama digital.',
    
    // Practice Area 2: Financial Markets
    practiceArea2Title: 'Mercados Financieros',
    practiceArea2Description: 'Asesoría legal en regulación de mercados de capitales, cumplimiento de valores, estructuración de fondos de inversión e instrumentos financieros. Orientación integral para entidades que navegan marcos regulatorios financieros.',
    
    // Practice Area 3: Legal Strategy
    practiceArea3Title: 'Estrategia Legal',
    practiceArea3Description: 'Planificación y consultoría legal estratégica para empresas e individuos. Desarrollo de estrategias legales integrales alineadas con objetivos empresariales y gestión de riesgos.',
    
    // Practice Area 4: Commercial Law
    practiceArea4Title: 'Derecho Comercial',
    practiceArea4Description: 'Apoyo legal integral para transacciones comerciales, contratos empresariales, cumplimiento corporativo y resolución de disputas comerciales. Asesoría para empresas de todos los tamaños.',
    
    // Practice Area 5: Corporate Law
    practiceArea5Title: 'Derecho Corporativo',
    practiceArea5Description: 'Estructuración corporativa, fusiones y adquisiciones, gobierno corporativo y cumplimiento. Asesoría legal estratégica para corporaciones, startups y entidades de mercados de capitales.',
    
    // Practice Area 6: Online Dispute Resolution
    practiceArea6Title: 'Resolución de Disputas en Línea',
    practiceArea6Description: 'Representación en plataformas de resolución de disputas en línea y basadas en blockchain. Experiencia en sistemas ODR, disputas de contratos inteligentes y protocolos de arbitraje descentralizados.',
    
    // Practice Area 7: Computational Law
    practiceArea7Title: 'Derecho Computacional',
    practiceArea7Description: 'Aplicación de métodos computacionales a problemas legales. Contratos inteligentes, regulación algorítmica, analítica legal y automatización de procesos de toma de decisiones legales.',
    
    // Practice Area 8: Artificial Intelligence Governance
    practiceArea8Title: 'Gobernanza de Inteligencia Artificial',
    practiceArea8Description: 'Marcos legales para el desarrollo e implementación de IA. Ética de IA, rendición de cuentas algorítmica, mitigación de sesgos y cumplimiento de regulaciones emergentes de IA.',
    
    // Practice Area 9: Crypto Governance
    practiceArea9Title: 'Gobernanza Cripto',
    practiceArea9Description: 'Asesoría legal en criptomonedas, blockchain, DeFi, NFTs y activos digitales. Cumplimiento regulatorio, tokenomics y estructuras de gobernanza para proyectos cripto.',
    
    // Footer
    footerDescription: 'Servicios legales profesionales con experiencia en derecho digital, regulación fintech y cumplimiento de mercados de capitales. Comprometido en proveer soluciones legales innovadoras en la intersección de tecnología y derecho.',
    quickLinks: 'Enlaces Rápidos',
    contactInfo: 'Información de Contacto',
    allRightsReserved: 'Todos los derechos reservados.',
    languageLabel: 'Idioma',
    footerLinksLabel: 'Enlaces del pie',
    privacyPolicy: 'Política de Privacidad',
    termsOfService: 'Términos de Servicio',
  },
  fr: {
    // Navigation
    home: 'Accueil',
    aboutMe: 'À Propos',
    practiceAreas: 'Domaines de Pratique',
    blog: 'Blog',
    multimedia: 'Multimedia',
    clerk: 'Clerk',
    contact: 'Contact',
    
    // Multimedia
    multimediaTitle: 'Multimedia',
    multimediaDescription: 'Événements, conférences et engagements professionnels.',
    multimediaEmpty: 'Aucun contenu pour le moment',
    multimediaEmptyHint: 'Ajoutez des vidéos dans public/videos/ et des images dans public/images/multimedia/, puis listez-les dans public/multimedia.json.',
    multimediaImageCompetitionLawDialogDo:
      '« Règles claires pour concurrencer : dialogue sur la réforme de la loi sur la concurrence en République dominicaine » 🇩🇴. Ce dialogue constitue un espace d\'apports précieux qui renforcent l\'analyse et la construction d\'un cadre de la concurrence plus solide et plus transparent.',
    
    // Article actions
    downloadPDF: 'Télécharger PDF',
    showCitation: 'Citer l\'Article',
    thesisDownloadLabel: 'Télécharger la thèse complète',
    thesisExcerptNote: 'Cet article est un extrait de mon mémoire de licence, que vous pouvez télécharger en version complète grâce au bouton ci-dessus.',
    citationModalTitle: 'Citation APA 7',
    citationFor: 'Citation pour:',
    copyCitation: 'Copier la Citation',
    copied: 'Copié!',
    citationNote: 'Cette citation suit le format APA 7e édition pour les articles de blog et les articles en ligne. Vérifiez toujours avec les directives de citation de votre institution.',
    downloadingPDF: 'Génération du PDF...',
    
    // Home Page
    greeting: 'Bonjour, je suis Joan Bisonó,',
    profession: 'Avocat et Consultant — Droit Numérique, Marchés Financiers et Régulation Économique',
    contactMe: 'Contactez-moi',
    
    // About Page
    aboutGreeting: 'Bonjour',
    aboutIm: 'je suis',
    aboutName: 'Joan Bisonó',
    aboutRole: 'Avocat et Consultant en Droit Numérique, Marchés Financiers et Régulation Économique',
    professionalProfile: 'Profil Professionnel',
    objective: 'Objectif',
    academicBackground: 'Formation Académique',
    scheduleCall: 'Planifier un Appel',
    latestPublication: 'Dernière Publication',
    clerkBannerLabel: 'Pour Claude et ChatGPT',
    clerkBannerTitle: 'Clerk',
    clerkBannerDescription: 'Jurisprudence et législation dominicaines, dans votre assistant',
    clerkBannerCta: 'Voir comment ça marche',
    
    // Professional Profile Content
    profilePara1: "Je suis Joan Bisonó, avocat diplômé Magna Cum Laude de la Pontificia Universidad Católica Madre y Maestra (PUCMM). J'ai de l'expérience en conformité réglementaire pour les entités des marchés de capitaux et en coordination d'activités juridiques et corporatives. J'ai travaillé comme Paralégal Senior au cabinet international ECIJA DVMS, gérant les relations avec des clients de haut profil et mettant en œuvre des solutions technologiques pour optimiser les processus juridiques. Je suis actuellement avocat chez Jorge Prats & Sousa Duverge Abogados | Consultores et poursuis un Master en Droit Administratif et Régulation Économique à la PUCMM.",
    profilePara2: "Je suis également chercheur et auteur académique avec des publications sur des sujets tels que la résolution des litiges blockchain, la réglementation fintech et l'adjudication algorithmique dans les plateformes de crypto-arbitrage.",
    
    // Objective Content
    objectiveText: "Mettre à profit mon expertise en droit numérique, marchés financiers et régulation économique pour fournir des solutions juridiques innovantes à l'intersection de la technologie et du droit. Mon objectif est d'appliquer mes connaissances en technologie réglementaire, blockchain et fintech pour aider les clients à naviguer dans le paysage juridique en évolution des technologies émergentes.",
    
    // Professional Info
    phone: 'Téléphone',
    email: 'Email',
    
    // Education
    pucmmName: 'Pontificia Universidad Católica Madre y Maestra',
    pucmmDegree: 'Licence en Droit',
    pucmmPeriod: 'Sep 2021 - Sep 2025 • Magna Cum Laude',
    omgName: 'Pontificia Universidad Católica Madre y Maestra (PUCMM)',
    omgDegree: 'Master en Droit Administratif et Régulation Économique',
    omgPeriod: 'Avr 2026 - Présent',
    
    // Key Credentials
    keyCredentials: 'Crédentials Clés',
    education: 'Éducation',
    educationMasters: 'PUCMM — Master en Droit Administratif et Régulation Économique',
    educationBachelors: 'PUCMM — Licence, Magna Cum Laude',
    currentRole: 'Rôle Actuel',
    currentRoleDescription: 'Avocat et Consultant chez Jorge Prats & Sousa Duverge Abogados | Consultores, spécialisé en Droit Numérique, Marchés Financiers et Régulation Économique',
    affiliations: 'Affiliations',
    affiliationKleros: 'Kleros Fellow — 9e Cohorte, Kleros Fellowship of Justice',
    affiliationBlf: 'Membre, Blockchain Lawyers Forum',
    
    // Profile Summary
    profileSummary: 'Avocat et Consultant en Droit Numérique, Marchés Financiers et Régulation Économique. Expertise en conformité réglementaire et marchés de capitaux.',
    focusAreas: 'Domaines de Focus:',
    focusArea1: 'Technologie réglementaire (RegTech) et supervision des écosystèmes fintech',
    focusArea2: 'Résolution des litiges blockchain et plateformes de crypto-arbitrage',
    focusArea3: 'Conformité réglementaire des marchés de capitaux et réglementation financière',
    focusArea4: 'Recherche académique sur les technologies émergentes en droit et réglementation financière',
    
    // Blog
    blogTitle: 'Blog',
    blogDescription: "Perspectives et articles sur le droit numérique, la résolution des litiges et l'analyse économique du droit",
    newArticle: 'Nouvel Article',
    articleTitle: "Titre de l'Article",
    articleContent: "Contenu de l'Article",
    publishArticle: 'Publier Article',
    cancel: 'Annuler',
    noArticles: "Aucun article publié pour l'instant",
    searchPlaceholder: 'Rechercher par titre, mots-clés...',
    noSearchResults: 'Aucun article trouvé. Essayez d\'autres termes.',
    paginationPage: 'Page',
    paginationOf: 'sur',
    goToPagePlaceholder: 'Aller à la page',
    goToPageButton: 'Aller',
    readMore: 'Lire Plus',
    backToBlog: 'Retour au Blog',
    seeMore: 'Voir Plus',
    seeLess: 'Voir Moins',
    moreArticles: 'Plus d\'Articles',
    articleBylinePrefix: 'Par',
    authorBio: 'est avocat et consultant spécialisé en Droit Numérique, Marchés Financiers, Régulation Économique, Legal Tech et Régulation et Gouvernance de l\'Intelligence Artificielle. Il est titulaire d\'une Licence en Droit (LL.B.) de la Pontificia Universidad Católica Madre y Maestra (PUCMM) et poursuit actuellement une Maîtrise (LL.M.) en Droit Administratif et Régulation Économique à la PUCMM. ORCID: 0009-0006-2365-1334.',
    authorAlisonBio: 'est avocate en exercice et chercheuse juridique au cabinet multinational ECIJA DVMS, spécialisée en contentieux civil et commercial et en arbitrage. Elle est titulaire d\'une Licence en Droit (LL.B.) de l\'Universidad Iberoamericana (UNIBE) et est actuellement candidate au Master (LL.M.) en procédure civile à la Pontificia Universidad Católica Madre y Maestra (PUCMM). Contact : alisonsofiaaa@gmail.com.',
    articleDisclaimer: 'Le contenu de cet article reflète mes opinions personnelles et ne reflète pas la politique ou la position officielle de mes employeurs actuels ou passés. De plus, les informations fournies sont uniquement à titre informatif et ne constituent pas un avis professionnel ou juridique.',
    writtenBy: 'Écrit par',
    copyLink: 'Copier le lien',
    linkCopied: 'Lien copié',
    blogTopics: 'Thèmes',
    blogAllTopics: 'Tous',
    blogAuthorBlurb: 'Avocat. Droit numérique, marchés financiers, régulation économique et legal tech.',
    
    
    // Contact Page
    contactDescription: 'Contactez-moi pour discuter de vos besoins juridiques et planifier une consultation.',
    getInTouch: 'Entrer en Contact',
    contactIntro: 'Que vous ayez besoin de conseils juridiques, de représentation ou que vous ayez des questions sur mes services, je suis là pour vous aider. N\'hésitez pas à me contacter par l\'un des canaux ci-dessous.',
    followMe: 'Suivez-moi sur les réseaux sociaux',
    scheduleConsultation: 'Planifier une Consultation',
    scheduleDescription: 'Réservez une consultation personnalisée pour discuter de vos affaires juridiques. J\'offre des horaires flexibles pour répondre à vos besoins.',
    callNow: 'Appeler Maintenant',
    sendEmail: 'Envoyer un Email',
    availability: 'Disponibilité',
    mondayFriday: 'Lundi - Vendredi',
    responseTime: 'Temps de Réponse',
    within24Hours: 'Dans les 24 heures',
    legalConsultation: 'Consultation Juridique',
    consultationDescription: 'Conseils juridiques professionnels adaptés à votre situation spécifique en matière de contentieux, arbitrage et résolution des litiges.',
    corporateServices: 'Services aux Entreprises',
    corporateDescription: 'Support juridique complet pour les entreprises, incluant la révision de contrats, la conformité et le contentieux commercial.',
    
    // Practice Areas Page
    practiceAreasDescription: 'Services juridiques innovants à l\'intersection du droit et de la technologie, spécialisés dans les technologies émergentes et la transformation numérique.',
    consultNow: 'Consulter Maintenant',
    needLegalAdvice: 'Besoin de Conseils Juridiques ?',
    needLegalAdviceDescription: 'Contactez-moi pour discuter de la manière dont je peux vous aider avec vos affaires juridiques.',
    
    // Practice Area 1: Digital Law
    practiceArea1Title: 'Droit Numérique',
    practiceArea1Description: 'Conseil juridique sur la transformation numérique, la protection des données, la cybersécurité, le commerce électronique et les droits numériques. Orientation complète pour les entreprises naviguant dans le paysage numérique.',
    
    // Practice Area 2: Financial Markets
    practiceArea2Title: 'Marchés Financiers',
    practiceArea2Description: 'Conseil juridique en régulation des marchés de capitaux, conformité des valeurs mobilières, structuration de fonds d\'investissement et instruments financiers. Orientation complète pour les entités naviguant dans les cadres réglementaires financiers.',
    
    // Practice Area 3: Legal Strategy
    practiceArea3Title: 'Stratégie Juridique',
    practiceArea3Description: 'Planification et conseil juridique stratégique pour les entreprises et les particuliers. Développement de stratégies juridiques complètes alignées sur les objectifs commerciaux et la gestion des risques.',
    
    // Practice Area 4: Commercial Law
    practiceArea4Title: 'Droit Commercial',
    practiceArea4Description: 'Support juridique complet pour les transactions commerciales, les contrats d\'entreprise, la conformité corporative et la résolution des litiges commerciaux. Conseil pour les entreprises de toutes tailles.',
    
    // Practice Area 5: Corporate Law
    practiceArea5Title: 'Droit des Sociétés',
    practiceArea5Description: 'Structuration corporative, fusions et acquisitions, gouvernance d\'entreprise et conformité. Conseil juridique stratégique pour les sociétés, startups et entités des marchés de capitaux.',
    
    // Practice Area 6: Online Dispute Resolution
    practiceArea6Title: 'Résolution de Litiges en Ligne',
    practiceArea6Description: 'Représentation dans les plateformes de résolution de litiges en ligne et basées sur la blockchain. Expertise dans les systèmes ODR, les litiges de contrats intelligents et les protocoles d\'arbitrage décentralisés.',
    
    // Practice Area 7: Computational Law
    practiceArea7Title: 'Droit Computationnel',
    practiceArea7Description: 'Application de méthodes computationnelles aux problèmes juridiques. Contrats intelligents, réglementation algorithmique, analyse juridique et automatisation des processus de prise de décision juridique.',
    
    // Practice Area 8: Artificial Intelligence Governance
    practiceArea8Title: 'Gouvernance de l\'Intelligence Artificielle',
    practiceArea8Description: 'Cadres juridiques pour le développement et le déploiement de l\'IA. Éthique de l\'IA, responsabilité algorithmique, atténuation des biais et conformité aux réglementations émergentes sur l\'IA.',
    
    // Practice Area 9: Crypto Governance
    practiceArea9Title: 'Gouvernance Crypto',
    practiceArea9Description: 'Conseil juridique sur les cryptomonnaies, la blockchain, DeFi, NFT et les actifs numériques. Conformité réglementaire, tokenomics et structures de gouvernance pour les projets crypto.',
    
    // Footer
    footerDescription: 'Services juridiques professionnels avec expertise en droit numérique, réglementation fintech et conformité des marchés de capitaux. Engagé à fournir des solutions juridiques innovantes à l\'intersection de la technologie et du droit.',
    quickLinks: 'Liens Rapides',
    contactInfo: 'Coordonnées',
    allRightsReserved: 'Tous droits réservés.',
    languageLabel: 'Langue',
    footerLinksLabel: 'Liens du pied de page',
    privacyPolicy: 'Politique de Confidentialité',
    termsOfService: 'Conditions de Service',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Helper: map ISO country code to one of our supported languages
const mapCountryToLanguage = (countryCode: string): Language => {
  const code = countryCode.toUpperCase();

  // Spanish‑speaking countries (non‑exhaustive but covers main ones)
  const spanishCountries = new Set([
    'DO', // Dominican Republic
    'ES', // Spain
    'MX', 'AR', 'CO', 'PE', 'CL', 'VE', 'EC', 'GT', 'CU', 'BO',
    'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY'
  ]);

  // French‑speaking countries (primary)
  const frenchCountries = new Set([
    'FR', // France
    'BE', 'CH', 'LU', 'CA' // Belgium, Switzerland, Luxembourg, Canada (partially)
  ]);

  if (spanishCountries.has(code)) return 'es';
  if (frenchCountries.has(code)) return 'fr';

  // Default: English
  return 'en';
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');

  // Persist language choice and allow manual overrides
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('language', lang);
      } catch {
        // ignore storage errors (private mode, etc.)
      }
    }
  };

  // On first load, detect preferred language:
  // 1) User preference from localStorage
  // 2) Geolocation by IP (country -> language map)
  // 3) Browser language (navigator.language)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1) Local preference
    try {
      const saved = window.localStorage.getItem('language') as Language | null;
      if (saved && ['en', 'es', 'fr'].includes(saved)) {
        setLanguageState(saved);
        return;
      }
    } catch {
      // ignore
    }

    // 2) IP-based geolocation (best-effort, non-blocking)
    const detectByIP = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 2500); // 2.5s safety

        // ipapi.co provides country_code without needing an API key (rate-limited but fine for personal site)
        const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
        clearTimeout(timeout);

        if (!res.ok) return;
        const data: { country_code?: string } = await res.json();

        if (data.country_code) {
          const lang = mapCountryToLanguage(data.country_code);
          setLanguageState(lang);
          try {
            window.localStorage.setItem('language', lang);
          } catch {
            // ignore
          }
          return;
        }
      } catch {
        // network errors: fall back to browser language
      }

      // 3) Browser language as last resort
      const browserLang =
        (navigator.language || navigator.languages?.[0] || 'en').slice(0, 2).toLowerCase();
      if (browserLang === 'es') setLanguageState('es');
      else if (browserLang === 'fr') setLanguageState('fr');
      else setLanguageState('en');
    };

    detectByIP();
  }, []);

  // Un lector de pantalla pronuncia según `<html lang>`: sin esto leía el
  // inglés y el francés con la fonética del español de `index.html`.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};